import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faIndustry, 
  faArrowLeft, 
  faSave, 
  faTimes, 
  faInfoCircle,
  faTable,
  faUserCheck,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import { getCustomers } from '../../../apis/customers';
import { getReviewers } from '../../../apis/reviewers';
import API_BASE_URL from '../../../config/api';

// Debug: Log API base URL
console.log('🔧 Cement Testing API - Base URL:', API_BASE_URL);

const CEMENT_TYPE_OPTIONS = ['PPC', 'PSC', 'OPC 33', 'OPC 43', 'OPC 53'];

const CementTestingForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPreview, setShowPreview] = useState(false);
  const [currentTest, setCurrentTest] = useState(1);
  const [selectedTests, setSelectedTests] = useState([]);
  const test1DefaultSet = useRef(false);
  const test2DefaultSet = useRef(false);
  const test3DefaultSet = useRef(false);
  const test4DefaultSet = useRef(false);
  const test5DefaultSet = useRef(false);

  // Customer selection state (same as Cube Testing)
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');

  // Handle selected tests from popup
  useEffect(() => {
    if (location.state && location.state.selectedTests) {
      setSelectedTests(location.state.selectedTests);
      // Set current test to first available test and track it as first test
      let firstTest = null;
      if (location.state.selectedTests.includes('fineness')) {
        firstTest = 1;
      } else if (location.state.selectedTests.includes('consistency')) {
        firstTest = 2;
      } else if (location.state.selectedTests.includes('settingTime')) {
        firstTest = 3;
      } else if (location.state.selectedTests.includes('soundness')) {
        firstTest = 4;
      } else if (location.state.selectedTests.includes('compressiveStrength')) {
        firstTest = 5;
      }
      if (firstTest) {
        setCurrentTest(firstTest);
        setFirstTestNumber(firstTest);
      }
    } else {
      // Default to all tests if no selection made
      setSelectedTests(['fineness', 'consistency', 'settingTime', 'soundness', 'compressiveStrength']);
      setCurrentTest(1);
      setFirstTestNumber(1);
    }
  }, [location.state]);

  // Fetch customers on component mount (same as Cube Testing)
  useEffect(() => {
    const fetchCustomersData = async () => {
      try {
        setCustomersLoading(true);
        setCustomersError(null);
        const customersData = await getCustomers();
        setCustomers(customersData);
        setFilteredCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomersError('Failed to load customers');
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomersData();
  }, []);

  // Filter customers based on dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
        (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Reviewer selection state (same as Cube Testing)
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  const [reviewerInfo, setReviewerInfo] = useState({
    name: 'Lalita S. Dussa',
    designation: 'Quality Manager',
    graduation: 'B.Tech.(Civil)'
  });

  // Fetch reviewers from database (same as Cube Testing)
  useEffect(() => {
    const fetchReviewers = async () => {
      setLoadingReviewers(true);
      try {
        const reviewersData = await getReviewers();
        setReviewers(reviewersData);
        
        // Set default reviewer if available
        if (reviewersData.length > 0) {
          const defaultReviewer = reviewersData[0];
          setReviewerInfo({
            name: defaultReviewer.name,
            designation: defaultReviewer.designation,
            graduation: defaultReviewer.graduation
          });
          
          // Update form data with default reviewer
          setFormData(prev => ({
            ...prev,
            reviewed_by: `${defaultReviewer.name} - ${defaultReviewer.designation}`
          }));
        }
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        // Fallback to hardcoded reviewers if API fails
        const fallbackReviewers = [
          { id: 1, name: 'Lalita S. Dussa', designation: 'Quality Manager', graduation: 'B.Tech.(Civil)' },
          { id: 2, name: 'Harsha Prakarsha Sangave', designation: 'Quality Manager', graduation: 'M.E(Civil-Structures)' },
          { id: 3, name: 'Amol A Adam', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 4, name: 'Aaquib J. Shaikh', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 5, name: 'Prakarsha A. Sangave', designation: 'Chief Executive Officer', graduation: 'M.E(Civil-Structures)' }
        ];
        setReviewers(fallbackReviewers);
        
        // Set default reviewer from fallback
        if (fallbackReviewers.length > 0) {
          const defaultReviewer = fallbackReviewers[0];
          setReviewerInfo({
            name: defaultReviewer.name,
            designation: defaultReviewer.designation,
            graduation: defaultReviewer.graduation
          });
          
          // Update form data with default reviewer
          setFormData(prev => ({
            ...prev,
            reviewed_by: `${defaultReviewer.name} - ${defaultReviewer.designation}`
          }));
        }
      } finally {
        setLoadingReviewers(false);
      }
    };

    fetchReviewers();
  }, []);

  // Add CSS styles for form-select to match Cube Testing design
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .form-select {
        background-color: #1C2333 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
      }
      
      .form-select:focus {
        background-color: #1C2333 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .form-select option {
        background-color: #1C2333 !important;
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Set default Test Method for Cement Testing - 1 (Specific Gravity test)
  useEffect(() => {
    if (currentTest === 1 && !test1DefaultSet.current) {
      setFormData(prev => ({
        ...prev,
        test_method: 'IS 4031 (Part II)'
      }));
      test1DefaultSet.current = true;
    }
    // Reset flag when leaving Test 1
    if (currentTest !== 1) {
      test1DefaultSet.current = false;
    }
  }, [currentTest]);

  // Set default Test Method for Cement Testing - 2 (Soundness test)
  useEffect(() => {
    if (currentTest === 2 && !test2DefaultSet.current) {
      setFormData(prev => ({
        ...prev,
        test_method: 'IS 4031 (Part 3)'
      }));
      test2DefaultSet.current = true;
    }
    // Reset flag when leaving Test 2
    if (currentTest !== 2) {
      test2DefaultSet.current = false;
    }
  }, [currentTest]);

  // Set default Test Method for Cement Testing - 3 (Fineness test)
  useEffect(() => {
    if (currentTest === 3 && !test3DefaultSet.current) {
      setFormData(prev => ({
        ...prev,
        test_method: 'IS 4031 (Part 1)'
      }));
      test3DefaultSet.current = true;
    }
    // Reset flag when leaving Test 3
    if (currentTest !== 3) {
      test3DefaultSet.current = false;
    }
  }, [currentTest]);

  // Clear Test Method for Cement Testing - 4 (Consistency & Setting Time test)
  useEffect(() => {
    if (currentTest === 4 && !test4DefaultSet.current) {
      setFormData(prev => ({
        ...prev,
        test_method: ''
      }));
      test4DefaultSet.current = true;
    }
    // Reset flag when leaving Test 4
    if (currentTest !== 4) {
      test4DefaultSet.current = false;
    }
  }, [currentTest]);

  // Clear Test Method for Cement Testing - 5 (Compressive Strength test)
  useEffect(() => {
    if (currentTest === 5 && !test5DefaultSet.current) {
      setFormData(prev => ({
        ...prev,
        test_method: ''
      }));
      test5DefaultSet.current = true;
    }
    // Reset flag when leaving Test 5
    if (currentTest !== 5) {
      test5DefaultSet.current = false;
    }
  }, [currentTest]);

  const [formData, setFormData] = useState({
    // General Information
    customer_id: '',
    sample_description: 'Cement',
    date_of_receipt: new Date().toISOString().split('T')[0],
    cement_type: '',
    test_method: '',
    sample_test_code: '',
    date_of_testing: new Date().toISOString().split('T')[0],
    cement_grade: '',
    environmental_conditions: '',
    humidity: '',
    date_of_casting: '',
    curing_conditions: '',
    machine_used_for_testing: '',
    age_of_cube: '',
    
    // Test Report Details
    customer_site_address: '',
    date_of_report: '',
    reference_number: '',
    date_of_material_receipt: '',
    type_grade_cement: '',
    url_number: '',
    job_code_number: '',
    period_of_testing: '',
    period_of_testing_start: '',
    period_of_testing_end: '',
    condition_of_sample: 'Acceptable',
    location_of_testing: 'Lab',
    
    // Test 1: Specific Gravity
    specific_gravity_liquid_01: '', weight_cement_01: '', initial_reading_01: '', final_reading_01: '',
    specific_gravity_liquid_02: '', weight_cement_02: '', initial_reading_02: '', final_reading_02: '',
    density_01: '', density_02: '', avg_combined: '',
    
    // Test 2: Soundness
    distance_27c_01: '', distance_boiler_01: '', expansion_01: '',
    distance_27c_02: '', distance_boiler_02: '', expansion_02: '',
    distance_27c_03: '', distance_boiler_03: '', expansion_03: '',
    avg_soundness: '',
    
    // Test 3: Fineness
    cement_weight_01: '', residue_weight_01: '', mass_percentage_01: '',
    cement_weight_02: '', residue_weight_02: '', mass_percentage_02: '',
    cement_weight_03: '', residue_weight_03: '', mass_percentage_03: '',
    avg_residue: '',
    
    // Form-5 fields (copied from AACBlocksForm)
    block_id_1: '', length_1: '', breadth_1: '', height_1: '', area_1: '', weight_1: '', density_1: '', load_max_1: '', compressive_strength_1: '',
    block_id_2: '', length_2: '', breadth_2: '', height_2: '', area_2: '', weight_2: '', density_2: '', load_max_2: '', compressive_strength_2: '',
    block_id_3: '', length_3: '', breadth_3: '', height_3: '', area_3: '', weight_3: '', density_3: '', load_max_3: '', compressive_strength_3: '',
    avg_load_max: '', avg_compressive_strength: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa - Quality Manager',
    authorized_by: 'Prakarsh Sangave',
    remarks: ''
  });

  const allTestTypes = [
    { id: 1, title: 'Fineness', description: 'Determination of Specific Gravity of Cement', testId: 'fineness' },
    { id: 2, title: 'Consistency', description: 'Determination of Soundness of Cement', testId: 'consistency' },
    { id: 3, title: 'Setting Time', description: 'Determination of Fineness of Cement by Dry Sieving', testId: 'settingTime' },
    { id: 4, title: 'Soundness', description: 'Standard Consistency Test & Initial and Final Setting Time', testId: 'soundness' },
    { id: 5, title: 'Compressive Strength', description: 'Determination of Compressive Strength of Cement/Mortar Cube', testId: 'compressiveStrength' }
  ];

  // Filter test types based on selected tests
  const testTypes = allTestTypes.filter(test => selectedTests.includes(test.testId));

  // Store first test's common data (General Info, Remarks, Verification) for auto-population
  const [firstTestCommonData, setFirstTestCommonData] = useState(null);
  const [cementTestRequestId, setCementTestRequestId] = useState(null);
  // Track which test number is the first test (first selected test)
  const [firstTestNumber, setFirstTestNumber] = useState(null);

  // Auto-populate General Info, Remarks, and Verification when switching to a new test
  useEffect(() => {
    // If we have first test's common data and we're on a different test, auto-populate
    if (firstTestCommonData && firstTestNumber && currentTest !== firstTestNumber) {
      setFormData(prev => {
        return {
          ...prev,
          // General Information - only if empty
          customer_id: prev.customer_id || firstTestCommonData.customer_id || '',
          sample_description: prev.sample_description || firstTestCommonData.sample_description || '',
          date_of_receipt: prev.date_of_receipt || firstTestCommonData.date_of_receipt || new Date().toISOString().split('T')[0],
          cement_type: prev.cement_type || firstTestCommonData.cement_type || '',
          cement_grade: prev.cement_grade || firstTestCommonData.cement_grade || '',
          environmental_conditions: prev.environmental_conditions || firstTestCommonData.environmental_conditions || '',
          humidity: prev.humidity || firstTestCommonData.humidity || '',
          reference_number: prev.reference_number || firstTestCommonData.reference_number || '',
          url_number: prev.url_number || firstTestCommonData.url_number || '',
          job_code_number: prev.job_code_number || firstTestCommonData.job_code_number || '',
          condition_of_sample: prev.condition_of_sample || firstTestCommonData.condition_of_sample || '',
          period_of_testing: prev.period_of_testing || firstTestCommonData.period_of_testing || '',
          location_of_testing: prev.location_of_testing || firstTestCommonData.location_of_testing || '',
          // Verification - always copy from first test
          tested_by_name: firstTestCommonData.tested_by_name || prev.tested_by_name || '',
          tested_by_date: firstTestCommonData.tested_by_date || prev.tested_by_date || new Date().toISOString().split('T')[0],
          checked_by_name: firstTestCommonData.checked_by_name || prev.checked_by_name || '',
          checked_by_date: firstTestCommonData.checked_by_date || prev.checked_by_date || new Date().toISOString().split('T')[0],
          verified_by_name: firstTestCommonData.verified_by_name || prev.verified_by_name || 'Prakarsh A Sangave',
          verified_by_date: firstTestCommonData.verified_by_date || prev.verified_by_date || new Date().toISOString().split('T')[0],
          reviewed_by: firstTestCommonData.reviewed_by || prev.reviewed_by || 'Lalita S. Dussa - Quality Manager',
          authorized_by: firstTestCommonData.authorized_by || prev.authorized_by || 'Prakarsh Sangave',
          // Remarks - always copy from first test
          remarks: firstTestCommonData.remarks || prev.remarks || ''
        };
      });
    }
  }, [currentTest, firstTestCommonData, firstTestNumber]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = {
      ...prev,
      [name]: value
      };
      // Auto-sync Sample Test Code Number with Job Code Number
      if (name === 'job_code_number') {
        updatedData.sample_test_code = value;
      }
      return updatedData;
    });
  };

  // Handle period of testing date range
  const handlePeriodOfTestingChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Format dates as DD-MM-YYYY
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      
      // Combine dates into "DD-MM-YYYY to DD-MM-YYYY" format
      if (updated.period_of_testing_start && updated.period_of_testing_end) {
        updated.period_of_testing = `${formatDate(updated.period_of_testing_start)} to ${formatDate(updated.period_of_testing_end)}`;
      } else if (updated.period_of_testing_start || updated.period_of_testing_end) {
        updated.period_of_testing = updated.period_of_testing_start || updated.period_of_testing_end;
      } else {
        updated.period_of_testing = '';
      }
      
      return updated;
    });
  };

  // Handle customer selection from dropdown (same as Liquid Admixture)
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id.toString(),
      customer_site_address: customer.address || customer.site_address || '' // Update address if available
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
    setDropdownSearchTerm(''); // Clear dropdown search
  };

  // Handle dropdown search input
  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  // Handle customer input click to show dropdown
  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm(''); // Clear search when opening dropdown
  };

  // Handle reviewer selection change (same as Cube Testing)
  const handleReviewerChange = (e) => {
    const selectedOption = e.target.options[e.target.selectedIndex];
    if (selectedOption.value) {
      const reviewerData = {
        name: selectedOption.getAttribute('data-name'),
        designation: selectedOption.getAttribute('data-designation'),
        graduation: selectedOption.getAttribute('data-graduation')
      };
      
      setReviewerInfo(reviewerData);
      
      // Update form data with reviewer information
      setFormData(prev => ({
        ...prev,
        reviewed_by: `${reviewerData.name} - ${reviewerData.designation}`
      }));
    }
  };

  const calcCementDensity = (suffix) => {
    const a = parseFloat(formData[`weight_cement_${suffix}`]);
    const b = parseFloat(formData[`initial_reading_${suffix}`]);
    const c = parseFloat(formData[`final_reading_${suffix}`]);
    if (Number.isNaN(a) || Number.isNaN(b) || Number.isNaN(c) || c - b === 0) {
      return '';
    }
    return (a / (c - b)).toFixed(3);
  };

  const calculateSpecificGravityAverages = () => {
    const density01 = calcCementDensity('01');
    const density02 = calcCementDensity('02');
    const densities = [density01, density02]
      .map((d) => parseFloat(d))
      .filter((d) => !Number.isNaN(d));
    const avgCombined = densities.length > 0
      ? (densities.reduce((sum, d) => sum + d, 0) / densities.length).toFixed(3)
      : '';

    setFormData((prev) => ({
      ...prev,
      density_01: density01,
      density_02: density02,
      avg_combined: avgCombined,
    }));
  };

  useEffect(() => {
    if (currentTest === 1) {
      calculateSpecificGravityAverages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTest,
    formData.specific_gravity_liquid_01,
    formData.specific_gravity_liquid_02,
    formData.weight_cement_01,
    formData.weight_cement_02,
    formData.initial_reading_01,
    formData.initial_reading_02,
    formData.final_reading_01,
    formData.final_reading_02,
  ]);

  const calculateSoundnessAverages = () => {
    const updates = {};
    const expansions = [];

    for (let i = 1; i <= 3; i++) {
      const suffix = `0${i}`;
      const a = parseFloat(formData[`distance_27c_${suffix}`]);
      const b = parseFloat(formData[`distance_boiler_${suffix}`]);

      if (!Number.isNaN(a) && !Number.isNaN(b)) {
        const expansion = b - a;
        updates[`expansion_${suffix}`] = expansion.toFixed(2);
        expansions.push(expansion);
      } else {
        updates[`expansion_${suffix}`] = '';
      }
    }

    updates.avg_soundness = expansions.length > 0
      ? (expansions.reduce((sum, value) => sum + value, 0) / expansions.length).toFixed(2)
      : '';

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  useEffect(() => {
    if (currentTest === 2) {
      calculateSoundnessAverages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTest,
    formData.distance_27c_01,
    formData.distance_27c_02,
    formData.distance_27c_03,
    formData.distance_boiler_01,
    formData.distance_boiler_02,
    formData.distance_boiler_03,
  ]);

  const calculateFinenessAverages = () => {
    const updates = {};
    const percentages = [];

    for (let i = 1; i <= 3; i++) {
      const suffix = `0${i}`;
      const a = parseFloat(formData[`cement_weight_${suffix}`]);
      const b = parseFloat(formData[`residue_weight_${suffix}`]);

      if (!Number.isNaN(a) && a > 0 && !Number.isNaN(b)) {
        const massPercentage = (b / a) * 100;
        updates[`mass_percentage_${suffix}`] = massPercentage.toFixed(2);
        percentages.push(massPercentage);
      } else {
        updates[`mass_percentage_${suffix}`] = '';
      }
    }

    updates.avg_residue = percentages.length > 0
      ? (percentages.reduce((sum, value) => sum + value, 0) / percentages.length).toFixed(2)
      : '';

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  useEffect(() => {
    if (currentTest === 3) {
      calculateFinenessAverages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTest,
    formData.cement_weight_01,
    formData.cement_weight_02,
    formData.cement_weight_03,
    formData.residue_weight_01,
    formData.residue_weight_02,
    formData.residue_weight_03,
  ]);

  const calculateStandardConsistency = () => {
    const cementWeight = parseFloat(formData.cement_weight_consistency_3);
    const waterWeight = parseFloat(formData.water_percentage_3);

    let standardConsistency = '';
    if (!Number.isNaN(cementWeight) && cementWeight > 0 && !Number.isNaN(waterWeight)) {
      standardConsistency = ((waterWeight / cementWeight) * 100).toFixed(2);
    }

    setFormData((prev) => ({
      ...prev,
      standard_consistency: standardConsistency,
    }));
  };

  useEffect(() => {
    if (currentTest === 4) {
      calculateStandardConsistency();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTest,
    formData.cement_weight_consistency_3,
    formData.water_percentage_3,
  ]);

  const calcCompressiveStrengthRow = (row) => {
    const length = parseFloat(formData[`length_${row}`]);
    const breadth = parseFloat(formData[`breadth_${row}`]);
    const height = parseFloat(formData[`height_${row}`]);
    const weight = parseFloat(formData[`weight_${row}`]);
    const loadMax = parseFloat(formData[`load_max_${row}`]);

    let area = '';
    let density = '';
    let compressiveStrength = '';

    if (!Number.isNaN(length) && !Number.isNaN(breadth) && length > 0 && breadth > 0) {
      area = (length * breadth).toFixed(2);
    }

    if (
      !Number.isNaN(length) && !Number.isNaN(breadth) && !Number.isNaN(height)
      && !Number.isNaN(weight) && length > 0 && breadth > 0 && height > 0
    ) {
      const volumeM3 = (length * breadth * height) / 1_000_000_000;
      if (volumeM3 > 0) {
        density = (weight / volumeM3).toFixed(1);
      }
    }

    const areaNum = parseFloat(area);
    if (!Number.isNaN(areaNum) && areaNum > 0 && !Number.isNaN(loadMax)) {
      compressiveStrength = (loadMax / areaNum).toFixed(2);
    }

    return { area, density, compressiveStrength };
  };

  const calculateCompressiveStrengthResults = () => {
    const updates = {};
    const strengths = [];

    for (let row = 1; row <= 3; row += 1) {
      const { area, density, compressiveStrength } = calcCompressiveStrengthRow(row);
      updates[`area_${row}`] = area;
      updates[`density_${row}`] = density;
      updates[`compressive_strength_${row}`] = compressiveStrength;
      const strengthNum = parseFloat(compressiveStrength);
      if (!Number.isNaN(strengthNum)) {
        strengths.push(strengthNum);
      }
    }

    updates.avg_compressive_strength = strengths.length > 0
      ? (strengths.reduce((sum, value) => sum + value, 0) / strengths.length).toFixed(2)
      : '';

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  useEffect(() => {
    if (currentTest === 5) {
      calculateCompressiveStrengthResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTest,
    formData.length_1,
    formData.breadth_1,
    formData.height_1,
    formData.weight_1,
    formData.load_max_1,
    formData.length_2,
    formData.breadth_2,
    formData.height_2,
    formData.weight_2,
    formData.load_max_2,
    formData.length_3,
    formData.breadth_3,
    formData.height_3,
    formData.weight_3,
    formData.load_max_3,
  ]);

  // Fill Random Data functionality
  const fillRandomData = () => {
    const rand = (min, max, d=2) => (Math.random()*(max-min)+min).toFixed(d);
    const randInt = (min, max) => Math.floor(Math.random()*(max-min+1)+min);
    
    // Generate random dates
    const randomPastDate = () => new Date(Date.now() - Math.random()*30*24*3600*1000).toISOString().slice(0,10);
    const randomTime = () => {
      const hours = String(randInt(8, 17)).padStart(2, '0');
      const minutes = String(randInt(0, 59)).padStart(2, '0');
      return `${hours}:${minutes}`;
    };
    
    // Generate period of testing dates
    const startDate = randomPastDate();
    const endDate = new Date(new Date(startDate).getTime() + randInt(1, 7)*24*3600*1000).toISOString().slice(0,10);
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    const periodOfTesting = `${formatDate(startDate)} to ${formatDate(endDate)}`;
    
    // Generate Test 4 time fields
    const test4TimeFields = {};
    for (let i = 1; i <= 3; i++) {
      test4TimeFields[`initial_time_${i}`] = randomTime();
      test4TimeFields[`initial_penetration_${i}`] = rand(5, 10, 1);
      test4TimeFields[`final_time_${i}`] = randomTime();
      test4TimeFields[`final_penetration_${i}`] = rand(5, 10, 1);
    }
    
    setFormData(prev => ({
      ...prev,
      // General Information
      customer_id: (1+Math.floor(Math.random()*3)).toString(),
      sample_description: 'OPC Cement Bag #' + randInt(1, 20),
      reference_number: 'REF-' + randInt(1000, 9999),
      date_of_receipt: randomPastDate(),
      cement_type: CEMENT_TYPE_OPTIONS[randInt(0, CEMENT_TYPE_OPTIONS.length - 1)],
      test_method: currentTest === 1 ? 'IS 4031 (Part II)' : currentTest === 2 ? 'IS 4031 (Part 3)' : currentTest === 3 ? 'IS 4031 (Part 1)' : '',
      sample_test_code: `CT-${new Date().getFullYear()}${randInt(100, 999)}`,
      date_of_testing: new Date().toISOString().slice(0,10),
      url_number: 'ULR-' + randInt(1000, 9999),
      job_code_number: 'JOB-' + randInt(10000, 99999),
      condition_of_sample: ['Good', 'Acceptable', 'Fair'][randInt(0, 2)],
      period_of_testing: periodOfTesting,
      period_of_testing_start: startDate,
      period_of_testing_end: endDate,
      location_of_testing: ['Lab A', 'Lab B', 'Main Laboratory'][randInt(0, 2)],
      cement_grade: ['33 Grade','43 Grade','53 Grade'][randInt(0, 2)],
      environmental_conditions: 'Lab Conditions - Temperature: ' + randInt(20, 30) + '°C',
      humidity: rand(45, 65, 1) + '%',
      
      // Test 5 specific fields
      date_of_casting: currentTest === 5 ? randomPastDate() : '',
      curing_conditions: currentTest === 5 ? ['Water Curing', 'Air Curing', 'Steam Curing'][randInt(0, 2)] : '',
      machine_used_for_testing: currentTest === 5 ? ['CTM 2000KN', 'CTM 3000KN', 'Universal Testing Machine'][randInt(0, 2)] : '',
      age_of_cube: currentTest === 5 ? ['3 days', '7 days', '28 days'][randInt(0, 2)] : '',
      
      // Test 1: Specific Gravity
      specific_gravity_liquid_01: rand(0.79, 0.81, 3),
      specific_gravity_liquid_02: rand(0.79, 0.81, 3),
      weight_cement_01: rand(50, 65, 3),
      weight_cement_02: rand(50, 65, 3),
      initial_reading_01: rand(0, 2, 1),
      initial_reading_02: rand(0, 2, 1),
      final_reading_01: rand(18, 22, 1),
      final_reading_02: rand(18, 22, 1),
      
      // Test 2: Soundness
      distance_27c_01: rand(10, 15, 2),
      distance_27c_02: rand(10, 15, 2),
      distance_27c_03: rand(10, 15, 2),
      distance_boiler_01: rand(12, 17, 2),
      distance_boiler_02: rand(12, 17, 2),
      distance_boiler_03: rand(12, 17, 2),
      
      // Test 3: Fineness
      cement_weight_01: 10,
      cement_weight_02: 10,
      cement_weight_03: 10,
      residue_weight_01: 0.5,
      residue_weight_02: 0.4,
      residue_weight_03: 0.4,
      
      // Test 4: Consistency & Setting Time
      cement_weight_consistency_1: rand(400, 500, 3),
      cement_weight_consistency_2: rand(400, 500, 3),
      cement_weight_consistency_3: rand(400, 500, 3),
      water_percentage_1: rand(100, 140, 1),
      water_percentage_2: rand(120, 150, 1),
      water_percentage_3: rand(130, 160, 1),
      penetration_height_1: rand(5, 7, 1),
      penetration_height_2: rand(5, 7, 1),
      penetration_height_3: rand(5, 7, 1),
      initial_observation: 'Initial setting time observed at ' + randomTime(),
      final_observation: 'Final setting time observed at ' + randomTime(),
      ...test4TimeFields,
      
      // Test 5: Compressive Strength
      length_1: rand(70, 71, 1),
      length_2: rand(70, 71, 1),
      length_3: rand(70, 71, 1),
      breadth_1: rand(70, 71, 1),
      breadth_2: rand(70, 71, 1),
      breadth_3: rand(70, 71, 1),
      height_1: rand(70, 71, 1),
      height_2: rand(70, 71, 1),
      height_3: rand(70, 71, 1),
      weight_1: rand(1.5, 2.5, 3),
      weight_2: rand(1.5, 2.5, 3),
      weight_3: rand(1.5, 2.5, 3),
      load_max_1: rand(200000, 350000, 0),
      load_max_2: rand(200000, 350000, 0),
      load_max_3: rand(200000, 350000, 0),
      
      // Verification
      tested_by_name: 'Demo User',
      tested_by_date: new Date().toISOString().slice(0,10),
      checked_by_name: 'Quality Check',
      checked_by_date: new Date().toISOString().slice(0,10),
      verified_by_name: 'Prakarsh A Sangave',
      verified_by_date: new Date().toISOString().slice(0,10),
      reviewed_by: 'Lalita S. Dussa - Quality Manager',
      authorized_by: 'Prakarsh Sangave',
      remarks: 'Demo generated data for testing purposes.'
    }));
  };

  // Save current test data directly to database
  const saveCurrentTestToDatabase = async () => {
    try {
      if (!formData.customer_id) {
        alert('Please select a customer.');
        return false;
      }

      let requestId = cementTestRequestId;

      // If this is the first test, create the request and save common report data
      if (currentTest === 1 && !requestId) {
        // Convert customer_id to integer
        const customerIdInt = parseInt(formData.customer_id, 10);
        if (isNaN(customerIdInt)) {
          alert('Invalid customer ID. Please select a valid customer.');
          return false;
        }
        
        // Verify customer exists in the loaded customers list
        const selectedCustomer = customers.find(c => c.id === customerIdInt);
        if (!selectedCustomer) {
          const availableIds = customers.map(c => c.id).join(', ');
          alert(`Selected customer (ID: ${customerIdInt}) is not in the available customers list.\n\nAvailable Customer IDs: ${availableIds || 'None'}\n\nPlease refresh the page and select a customer from the dropdown.`);
          return false;
        }
        
        // Create cement test request
        const createResponse = await fetch(`${API_BASE_URL}/cement-testing/create-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customerIdInt,
            test_request_id: null,
            selected_tests: selectedTests
          })
        });

        if (!createResponse.ok) {
          // Check if response is JSON before trying to parse
          const contentType = createResponse.headers.get('content-type');
          let errorMessage = `Failed to create test request (Status: ${createResponse.status})`;
          let errorData = {};
          
          if (contentType && contentType.includes('application/json')) {
            try {
              errorData = await createResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (jsonError) {
              // If JSON parsing fails, try to get text
              const textResponse = await createResponse.text();
              errorMessage = textResponse || errorMessage;
            }
          } else {
            // Response is not JSON (likely HTML error page)
            try {
              const textResponse = await createResponse.text();
              if (textResponse.includes('Page not found') || textResponse.includes('404')) {
                errorMessage = `API endpoint not found. Please check if the backend route exists: /api/cement-testing/create-request`;
              } else if (textResponse.length < 200) {
                errorMessage = textResponse;
              } else {
                errorMessage = `Server returned non-JSON response (Status: ${createResponse.status}). The endpoint may not exist or the server may be misconfigured.`;
              }
            } catch (textError) {
              errorMessage = `Failed to read error response (Status: ${createResponse.status})`;
            }
          }
          
          // Add helpful message if customer doesn't exist
          if (errorData.available_customers && errorData.available_customers.length > 0) {
            errorMessage += `\n\nAvailable Customers:\n${errorData.available_customers.join('\n')}`;
            errorMessage += '\n\nPlease refresh the page and select a customer from the dropdown that exists in the database.';
          } else if (errorData.available_customer_ids && errorData.available_customer_ids.length > 0) {
            errorMessage += `\n\nAvailable Customer IDs: ${errorData.available_customer_ids.join(', ')}`;
            errorMessage += '\n\nPlease refresh the page and select a customer from the dropdown.';
          } else if (errorMessage.includes('does not exist')) {
            errorMessage += '\n\nPlease refresh the page and select a valid customer from the dropdown.';
          }
          
          throw new Error(errorMessage);
        }

        const createResult = await createResponse.json();
        requestId = createResult.cement_test_request_id;
        setCementTestRequestId(requestId);

        // Save common report data
        const commonReportData = {
          customer_id: customerIdInt,
          reference_number: formData.reference_number || '',
          date_of_receipt: formData.date_of_receipt || '',
          url_number: formData.url_number || '',
          type_of_cement: formData.cement_type || '',
          grade_of_cement: formData.cement_grade || '',
          job_code_number: formData.job_code_number || '',
          condition_of_sample: formData.condition_of_sample || '',
          period_of_testing: formData.period_of_testing || '',
          environmental_conditions: formData.environmental_conditions || '',
          location_of_testing: formData.location_of_testing || '',
          humidity: formData.humidity || ''
        };

        const reportResponse = await fetch(`${API_BASE_URL}/cement-testing/${requestId}/common-report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(commonReportData)
        });

        if (!reportResponse.ok) {
          // Check if response is JSON before trying to parse
          const contentType = reportResponse.headers.get('content-type');
          let errorMessage = `Failed to save common report data (Status: ${reportResponse.status})`;
          
          if (contentType && contentType.includes('application/json')) {
            try {
              const errorData = await reportResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (jsonError) {
              // If JSON parsing fails, try to get text
              const textResponse = await reportResponse.text();
              errorMessage = textResponse || errorMessage;
            }
          } else {
            // Response is not JSON (likely HTML error page)
            try {
              const textResponse = await reportResponse.text();
              if (textResponse.includes('Page not found') || textResponse.includes('404')) {
                errorMessage = `API endpoint not found. Please check if the backend route exists: /api/cement-testing/${requestId}/common-report`;
              } else if (textResponse.length < 200) {
                errorMessage = textResponse;
              } else {
                errorMessage = `Server returned non-JSON response (Status: ${reportResponse.status}). The endpoint may not exist or the server may be misconfigured.`;
              }
            } catch (textError) {
              errorMessage = `Failed to read error response (Status: ${reportResponse.status})`;
            }
          }
          
          throw new Error(errorMessage);
        }

        // Store first test's common data for auto-population
        // If firstTestNumber is not set yet, set it to current test
        if (!firstTestNumber) {
          setFirstTestNumber(currentTest);
        }
        setFirstTestCommonData({
          customer_id: formData.customer_id,
          sample_description: formData.sample_description,
          date_of_receipt: formData.date_of_receipt,
          cement_type: formData.cement_type,
          cement_grade: formData.cement_grade,
          environmental_conditions: formData.environmental_conditions,
          humidity: formData.humidity,
          reference_number: formData.reference_number,
          url_number: formData.url_number,
          job_code_number: formData.job_code_number,
          condition_of_sample: formData.condition_of_sample,
          period_of_testing: formData.period_of_testing,
          location_of_testing: formData.location_of_testing,
          tested_by_name: formData.tested_by_name,
          tested_by_date: formData.tested_by_date,
          checked_by_name: formData.checked_by_name,
          checked_by_date: formData.checked_by_date,
          verified_by_name: formData.verified_by_name,
          verified_by_date: formData.verified_by_date,
          reviewed_by: formData.reviewed_by,
          authorized_by: formData.authorized_by,
          remarks: formData.remarks
        });
      } else if (currentTest === 1 && requestId) {
        // Update common report data if first test is being saved again
        const commonReportData = {
          customer_id: formData.customer_id,
          reference_number: formData.reference_number || '',
          date_of_receipt: formData.date_of_receipt || '',
          url_number: formData.url_number || '',
          type_of_cement: formData.cement_type || '',
          grade_of_cement: formData.cement_grade || '',
          job_code_number: formData.job_code_number || '',
          condition_of_sample: formData.condition_of_sample || '',
          period_of_testing: formData.period_of_testing || '',
          environmental_conditions: formData.environmental_conditions || '',
          location_of_testing: formData.location_of_testing || '',
          humidity: formData.humidity || ''
        };

        const reportResponse = await fetch(`${API_BASE_URL}/cement-testing/${requestId}/common-report`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(commonReportData)
        });

        if (!reportResponse.ok) {
          // Check if response is JSON before trying to parse
          const contentType = reportResponse.headers.get('content-type');
          let errorMessage = `Failed to update common report data (Status: ${reportResponse.status})`;
          
          if (contentType && contentType.includes('application/json')) {
            try {
              const errorData = await reportResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (jsonError) {
              // If JSON parsing fails, try to get text
              const textResponse = await reportResponse.text();
              errorMessage = textResponse || errorMessage;
            }
          } else {
            // Response is not JSON (likely HTML error page)
            try {
              const textResponse = await reportResponse.text();
              if (textResponse.includes('Page not found') || textResponse.includes('404')) {
                errorMessage = `API endpoint not found. Please check if the backend route exists: /api/cement-testing/${requestId}/common-report`;
              } else if (textResponse.length < 200) {
                errorMessage = textResponse;
              } else {
                errorMessage = `Server returned non-JSON response (Status: ${reportResponse.status}). The endpoint may not exist or the server may be misconfigured.`;
              }
            } catch (textError) {
              errorMessage = `Failed to read error response (Status: ${reportResponse.status})`;
            }
          }
          
          throw new Error(errorMessage);
        }
      }

      // If requestId is not set and we're not on Test 1, we need to create the request first
      if (!requestId && currentTest !== 1) {
        // Try to get customer_id from formData
        if (!formData.customer_id) {
          throw new Error('Please select a customer first.');
        }
        
        // Convert customer_id to integer
        const customerIdInt = parseInt(formData.customer_id, 10);
        if (isNaN(customerIdInt)) {
          throw new Error('Invalid customer ID. Please select a valid customer.');
        }
        
        // Verify customer exists in the loaded customers list
        const selectedCustomer = customers.find(c => c.id === customerIdInt);
        if (!selectedCustomer) {
          const availableIds = customers.map(c => c.id).join(', ');
          throw new Error(`Selected customer (ID: ${customerIdInt}) is not in the available customers list.\n\nAvailable Customer IDs: ${availableIds || 'None'}\n\nPlease refresh the page and select a customer from the dropdown.`);
        }
        
        // Create cement test request
        const createResponse = await fetch(`${API_BASE_URL}/cement-testing/create-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer_id: customerIdInt,
            test_request_id: null,
            selected_tests: selectedTests
          })
        });

        if (!createResponse.ok) {
          // Check if response is JSON before trying to parse
          const contentType = createResponse.headers.get('content-type');
          let errorMessage = `Failed to create test request (Status: ${createResponse.status})`;
          let errorData = {};
          
          if (contentType && contentType.includes('application/json')) {
            try {
              errorData = await createResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (jsonError) {
              // If JSON parsing fails, try to get text
              const textResponse = await createResponse.text();
              errorMessage = textResponse || errorMessage;
            }
          } else {
            // Response is not JSON (likely HTML error page)
            try {
              const textResponse = await createResponse.text();
              if (textResponse.includes('Page not found') || textResponse.includes('404')) {
                errorMessage = `API endpoint not found. Please check if the backend route exists: /api/cement-testing/create-request`;
              } else if (textResponse.length < 200) {
                errorMessage = textResponse;
              } else {
                errorMessage = `Server returned non-JSON response (Status: ${createResponse.status}). The endpoint may not exist or the server may be misconfigured.`;
              }
            } catch (textError) {
              errorMessage = `Failed to read error response (Status: ${createResponse.status})`;
            }
          }
          
          // Add helpful message if customer doesn't exist
          if (errorData.available_customers && errorData.available_customers.length > 0) {
            errorMessage += `\n\nAvailable Customers:\n${errorData.available_customers.join('\n')}`;
            errorMessage += '\n\nPlease refresh the page and select a customer from the dropdown that exists in the database.';
          } else if (errorData.available_customer_ids && errorData.available_customer_ids.length > 0) {
            errorMessage += `\n\nAvailable Customer IDs: ${errorData.available_customer_ids.join(', ')}`;
            errorMessage += '\n\nPlease refresh the page and select a customer from the dropdown.';
          } else if (errorMessage.includes('does not exist')) {
            errorMessage += '\n\nPlease refresh the page and select a valid customer from the dropdown.';
          }
          
          throw new Error(errorMessage);
        }

        const createResult = await createResponse.json();
        requestId = createResult.cement_test_request_id;
        setCementTestRequestId(requestId);

        // Save common report data (use data from first test if available, otherwise current test)
        const commonReportData = {
          customer_id: customerIdInt,
          reference_number: formData.reference_number || '',
          date_of_receipt: formData.date_of_receipt || '',
          url_number: formData.url_number || '',
          type_of_cement: formData.cement_type || '',
          grade_of_cement: formData.cement_grade || '',
          job_code_number: formData.job_code_number || '',
          condition_of_sample: formData.condition_of_sample || '',
          period_of_testing: formData.period_of_testing || '',
          environmental_conditions: formData.environmental_conditions || '',
          location_of_testing: formData.location_of_testing || '',
          humidity: formData.humidity || ''
        };

        const reportResponse = await fetch(`${API_BASE_URL}/cement-testing/${requestId}/common-report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(commonReportData)
        });

        if (!reportResponse.ok) {
          // Check if response is JSON before trying to parse
          const contentType = reportResponse.headers.get('content-type');
          let errorMessage = `Failed to save common report data (Status: ${reportResponse.status})`;
          
          if (contentType && contentType.includes('application/json')) {
            try {
              const errorData = await reportResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (jsonError) {
              // If JSON parsing fails, try to get text
              const textResponse = await reportResponse.text();
              errorMessage = textResponse || errorMessage;
            }
          } else {
            // Response is not JSON (likely HTML error page)
            try {
              const textResponse = await reportResponse.text();
              if (textResponse.includes('Page not found') || textResponse.includes('404')) {
                errorMessage = `API endpoint not found. Please check if the backend route exists: /api/cement-testing/${requestId}/common-report`;
              } else if (textResponse.length < 200) {
                errorMessage = textResponse;
              } else {
                errorMessage = `Server returned non-JSON response (Status: ${reportResponse.status}). The endpoint may not exist or the server may be misconfigured.`;
              }
            } catch (textError) {
              errorMessage = `Failed to read error response (Status: ${reportResponse.status})`;
            }
          }
          
          throw new Error(errorMessage);
        }
      }
      
      if (!requestId) {
        throw new Error('Cement test request ID not found. Please save Test 1 first or select a customer.');
      }

      // Map current test to backend endpoint
      const testEndpoints = {
        1: `test-1-specific-gravity`,
        2: `test-2-soundness`,
        3: `test-3-fineness`,
        4: `test-4-setting-time`,
        5: `test-5-compressive-strength`
      };

      const endpoint = testEndpoints[currentTest];
      if (!endpoint) {
        throw new Error(`Invalid test number: ${currentTest}`);
      }

      // Prepare test-specific data based on current test
      let testData = {};
      
      if (currentTest === 1) {
        // Specific Gravity
        testData = {
          sample_description: formData.sample_description,
          sample_test_code: formData.sample_test_code,
          date_of_testing: formData.date_of_testing,
          test_method: formData.test_method || 'IS 4031 (Part II)',
          specific_gravity_liquid_01: formData.specific_gravity_liquid_01,
          specific_gravity_liquid_02: formData.specific_gravity_liquid_02,
          weight_cement_01: formData.weight_cement_01,
          weight_cement_02: formData.weight_cement_02,
          initial_reading_01: formData.initial_reading_01,
          initial_reading_02: formData.initial_reading_02,
          final_reading_01: formData.final_reading_01,
          final_reading_02: formData.final_reading_02,
          density_01: formData.density_01,
          density_02: formData.density_02,
          avg_combined: formData.avg_combined,
          tested_by_name: formData.tested_by_name,
          tested_by_date: formData.tested_by_date,
          checked_by_name: formData.checked_by_name,
          checked_by_date: formData.checked_by_date,
          verified_by_name: formData.verified_by_name,
          verified_by_date: formData.verified_by_date,
          reviewed_by: formData.reviewed_by,
          authorized_by: formData.authorized_by,
          remarks: formData.remarks
        };
      } else if (currentTest === 2) {
        // Soundness
        testData = {
          sample_description: formData.sample_description,
          sample_test_code: formData.sample_test_code,
          date_of_testing: formData.date_of_testing,
          test_method: formData.test_method || 'IS 4031 (Part 3)',
          distance_27c_01: formData.distance_27c_01,
          distance_27c_02: formData.distance_27c_02,
          distance_27c_03: formData.distance_27c_03,
          distance_boiler_01: formData.distance_boiler_01,
          distance_boiler_02: formData.distance_boiler_02,
          distance_boiler_03: formData.distance_boiler_03,
          expansion_01: formData.expansion_01,
          expansion_02: formData.expansion_02,
          expansion_03: formData.expansion_03,
          avg_soundness: formData.avg_soundness,
          tested_by_name: formData.tested_by_name,
          tested_by_date: formData.tested_by_date,
          checked_by_name: formData.checked_by_name,
          checked_by_date: formData.checked_by_date,
          verified_by_name: formData.verified_by_name,
          verified_by_date: formData.verified_by_date,
          reviewed_by: formData.reviewed_by,
          authorized_by: formData.authorized_by,
          remarks: formData.remarks
        };
      } else if (currentTest === 3) {
        // Fineness
        testData = {
          sample_description: formData.sample_description,
          sample_test_code: formData.sample_test_code,
          date_of_testing: formData.date_of_testing,
          test_method: formData.test_method || 'IS 4031 (Part I)',
          cement_weight_01: formData.cement_weight_01,
          cement_weight_02: formData.cement_weight_02,
          cement_weight_03: formData.cement_weight_03,
          residue_weight_01: formData.residue_weight_01,
          residue_weight_02: formData.residue_weight_02,
          residue_weight_03: formData.residue_weight_03,
          mass_percentage_01: formData.mass_percentage_01,
          mass_percentage_02: formData.mass_percentage_02,
          mass_percentage_03: formData.mass_percentage_03,
          avg_residue: formData.avg_residue,
          tested_by_name: formData.tested_by_name,
          tested_by_date: formData.tested_by_date,
          checked_by_name: formData.checked_by_name,
          checked_by_date: formData.checked_by_date,
          verified_by_name: formData.verified_by_name,
          verified_by_date: formData.verified_by_date,
          reviewed_by: formData.reviewed_by,
          authorized_by: formData.authorized_by,
          remarks: formData.remarks
        };
      } else if (currentTest === 4) {
        // Setting Time
        // Include all time and penetration/impression fields
        testData = {
          sample_description: formData.sample_description,
          sample_test_code: formData.sample_test_code,
          date_of_testing: formData.date_of_testing,
          test_method: formData.test_method,
          temperature: formData.temperature,
          // Standard Consistency Test data
          standard_consistency_01: formData.standard_consistency || formData.standard_consistency_1 || null,
          standard_consistency_02: formData.standard_consistency || formData.standard_consistency_2 || null,
          standard_consistency_03: formData.standard_consistency || formData.standard_consistency_3 || null,
          // Standard Consistency Test table fields
          cement_weight_consistency_1: formData.cement_weight_consistency_1 || null,
          cement_weight_consistency_2: formData.cement_weight_consistency_2 || null,
          cement_weight_consistency_3: formData.cement_weight_consistency_3 || null,
          water_percentage_1: formData.water_percentage_1 || null,
          water_percentage_2: formData.water_percentage_2 || null,
          water_percentage_3: formData.water_percentage_3 || null,
          penetration_height_1: formData.penetration_height_1 || null,
          penetration_height_2: formData.penetration_height_2 || null,
          penetration_height_3: formData.penetration_height_3 || null,
          // Initial Setting Time
          initial_observation: formData.initial_observation,
          ...Object.fromEntries(
            Array.from({ length: 3 }, (_, i) => [
              `initial_time_${i + 1}`,
              formData[`initial_time_${i + 1}`] || null
            ])
          ),
          ...Object.fromEntries(
            Array.from({ length: 3 }, (_, i) => [
              `initial_penetration_${i + 1}`,
              formData[`initial_penetration_${i + 1}`] || null
            ])
          ),
          // Final Setting Time
          final_observation: formData.final_observation,
          ...Object.fromEntries(
            Array.from({ length: 3 }, (_, i) => [
              `final_time_${i + 1}`,
              formData[`final_time_${i + 1}`] || null
            ])
          ),
          ...Object.fromEntries(
            Array.from({ length: 3 }, (_, i) => [
              `final_impression_${i + 1}`,
              formData[`final_penetration_${i + 1}`] || formData[`final_impression_${i + 1}`] || null
            ])
          ),
          tested_by_name: formData.tested_by_name,
          tested_by_date: formData.tested_by_date,
          checked_by_name: formData.checked_by_name,
          checked_by_date: formData.checked_by_date,
          verified_by_name: formData.verified_by_name,
          verified_by_date: formData.verified_by_date,
          reviewed_by: formData.reviewed_by,
          authorized_by: formData.authorized_by,
          remarks: formData.remarks
        };
      } else if (currentTest === 5) {
        // Compressive Strength
        // Map form fields (length_1, breadth_1, etc.) to database fields (size_01, area_01, etc.)
        // Form only has 3 cubes, but database supports 6
        const calculateSize = (length, breadth) => {
          if (length && breadth) {
            return `${length} x ${breadth}`;
          }
          return null;
        };
        
        const calculateArea = (length, breadth) => {
          if (length && breadth) {
            return parseFloat(length) * parseFloat(breadth);
          }
          return null;
        };
        
        testData = {
          sample_description: formData.sample_description,
          sample_test_code: formData.sample_test_code,
          date_of_testing: formData.date_of_testing,
          test_method: formData.test_method,
          date_of_casting: formData.date_of_casting,
          age_of_cube: formData.age_of_cube,
          curing_conditions: formData.curing_conditions,
          machine_used_for_testing: formData.machine_used_for_testing,
          size_01: calculateSize(formData.length_1, formData.breadth_1),
          size_02: calculateSize(formData.length_2, formData.breadth_2),
          size_03: calculateSize(formData.length_3, formData.breadth_3),
          size_04: null, // Form only has 3 cubes
          size_05: null,
          size_06: null,
          area_01: formData.area_1 || calculateArea(formData.length_1, formData.breadth_1),
          area_02: formData.area_2 || calculateArea(formData.length_2, formData.breadth_2),
          area_03: formData.area_3 || calculateArea(formData.length_3, formData.breadth_3),
          area_04: null,
          area_05: null,
          area_06: null,
          weight_01: formData.weight_1,
          weight_02: formData.weight_2,
          weight_03: formData.weight_3,
          weight_04: null,
          weight_05: null,
          weight_06: null,
          load_max_01: formData.load_max_1,
          load_max_02: formData.load_max_2,
          load_max_03: formData.load_max_3,
          load_max_04: null,
          load_max_05: null,
          load_max_06: null,
          compressive_strength_01: formData.compressive_strength_1,
          compressive_strength_02: formData.compressive_strength_2,
          compressive_strength_03: formData.compressive_strength_3,
          compressive_strength_04: null,
          compressive_strength_05: null,
          compressive_strength_06: null,
          avg_compressive_strength: formData.avg_compressive_strength,
          tested_by_name: formData.tested_by_name,
          tested_by_date: formData.tested_by_date,
          checked_by_name: formData.checked_by_name,
          checked_by_date: formData.checked_by_date,
          verified_by_name: formData.verified_by_name,
          verified_by_date: formData.verified_by_date,
          reviewed_by: formData.reviewed_by,
          authorized_by: formData.authorized_by,
          remarks: formData.remarks
        };
      }

      // Save test data to database
      const response = await fetch(`${API_BASE_URL}/cement-testing/${requestId}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testData)
      });

      if (!response.ok) {
        // Check if response is JSON before trying to parse
        const contentType = response.headers.get('content-type');
        let errorMessage = `Failed to save test data (Status: ${response.status})`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (jsonError) {
            // If JSON parsing fails, try to get text
            const textResponse = await response.text();
            errorMessage = textResponse || errorMessage;
          }
        } else {
          // Response is not JSON (likely HTML error page)
          try {
            const textResponse = await response.text();
            // Extract meaningful error from HTML if possible
            if (textResponse.includes('Page not found') || textResponse.includes('404')) {
              errorMessage = `API endpoint not found. Please check if the backend route exists: /api/cement-testing/${requestId}/${endpoint}`;
            } else if (textResponse.length < 200) {
              // If response is short, it might be a plain text error
              errorMessage = textResponse;
            } else {
              errorMessage = `Server returned non-JSON response (Status: ${response.status}). The endpoint may not exist or the server may be misconfigured.`;
            }
          } catch (textError) {
            errorMessage = `Failed to read error response (Status: ${response.status})`;
          }
        }
        
        throw new Error(errorMessage);
      }

      // Parse successful response
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          await response.json(); // Consume the response
        } catch (jsonError) {
          // Response might be empty, which is fine
          console.log('Response body is empty or not JSON, continuing...');
        }
      }

      // Return the requestId so it can be used for navigation
      return requestId;
    } catch (error) {
      console.error('Error saving test data:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const savedRequestId = await saveCurrentTestToDatabase();
      
      // If saveCurrentTestToDatabase returned false, it means validation failed
      if (savedRequestId === false) {
        return; // Don't proceed if validation failed
      }
      
      alert(`Cement Testing - ${currentTest} data saved successfully to database!`);
      
      // Use the returned requestId or fallback to state
      const finalRequestId = savedRequestId || cementTestRequestId;
      
      // Move to next test if not the last one
      const currentTestIndex = testTypes.findIndex(t => t.id === currentTest);
      if (currentTestIndex < testTypes.length - 1) {
        // Move to next test
        setCurrentTest(testTypes[currentTestIndex + 1].id);
      } else {
        // Last test - navigate to test results
        console.log('🔍 Navigating to test results with requestId:', finalRequestId);
        navigate('/test-results', {
          state: {
            selectedTests,
            module: 'cement',
            cement_test_request_id: finalRequestId
          }
        });
      }
    } catch (error) {
      alert(`Error saving to database: ${error.message}`);
    }
  };

  const handleConfirmSubmit = () => {
    console.log('Form submitted:', formData);
    alert('Test data saved successfully!');
    setShowPreview(false);
    // Navigate to unified test results page with cement context
    console.log('🔍 Navigating to test results with requestId:', cementTestRequestId);
    navigate('/test-results', {
      state: {
        selectedTests,
        module: 'cement',
        cement_test_request_id: cementTestRequestId
      }
    });
  };

  const handleEditForm = () => {
    setShowPreview(false);
  };

  const renderTestNavigation = () => (
    <Row className="mb-2">
      <Col>
        <Card style={{ border: 'none' }}>
          <Card.Body className="py-2">
            <div className="d-flex justify-content-center">
              {testTypes.map((test) => (
                <Button
                  key={test.id}
                  variant={currentTest === test.id ? "primary" : "outline-primary"}
                  size="lg"
                  className="mx-2"
                  style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold', 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => setCurrentTest(test.id)}
                >
                  {test.id}
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderTest1SpecificGravity = () => (
    <Row className="mb-4">
      <Col>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faTable} className="me-2" />
              Test Results
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '350px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Number of Tests</th>
                    <th style={{ width: '120px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>1</th>
                    <th style={{ width: '120px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>2</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Specific Gravity of the liquid (kerosine)</td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="specific_gravity_liquid_01" value={formData.specific_gravity_liquid_01} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="specific_gravity_liquid_02" value={formData.specific_gravity_liquid_02} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Weight of cement taken for determination of specific gravity (A)(gm)</td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="weight_cement_01" value={formData.weight_cement_01} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="weight_cement_02" value={formData.weight_cement_02} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Initial Reading on the flask (B)(ml)</td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.1" name="initial_reading_01" value={formData.initial_reading_01} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.1" name="initial_reading_02" value={formData.initial_reading_02} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Final Reading on the flask after cement is added (C)(ml)</td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.1" name="final_reading_01" value={formData.final_reading_01} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.1" name="final_reading_02" value={formData.final_reading_02} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Density (g/cm³) (A/(C-B))</td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="density_01" value={formData.density_01} readOnly style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                    </td>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="density_02" value={formData.density_02} readOnly style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                    </td>
                  </tr>
                  <tr className="table-info">
                    <td className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', fontSize: '1.1rem' }}>Average</td>
                    <td colSpan="2" className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}>
                      <Form.Control size="sm" type="number" step="0.001" name="avg_combined" value={formData.avg_combined} readOnly className="form-control-vitrag" style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderTest2Soundness = () => (
    <Row className="mb-4">
      <Col>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faTable} className="me-2" />
              Test Results
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '55%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Number of Tests</th>
                    <th style={{ width: '15%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>1</th>
                    <th style={{ width: '15%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>2</th>
                    <th style={{ width: '15%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>3</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>(A) Distance between the indicator points keeping @ 27 ± 2°C for 24 hours (mm)</td>
                    {[1,2,3].map(i => (
                      <td key={i} style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control size="sm" type="number" step="0.01" name={`distance_27c_0${i}`} value={formData[`distance_27c_0${i}`]} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>(B) Distance between the indicator points after boiler for 3 hours (mm)</td>
                    {[1,2,3].map(i => (
                      <td key={i} style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control size="sm" type="number" step="0.01" name={`distance_boiler_0${i}`} value={formData[`distance_boiler_0${i}`]} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Expansion of Cement (B - A) (mm)</td>
                    {[1,2,3].map(i => (
                      <td key={i} style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control size="sm" type="number" step="0.01" name={`expansion_0${i}`} value={formData[`expansion_0${i}`]} readOnly style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                      </td>
                    ))}
                  </tr>
                  <tr className="table-info">
                    <td className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', fontSize: '1.1rem' }}>Average Soundness of Cement</td>
                    <td colSpan="3" className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}>
                      <Form.Control size="sm" type="number" step="0.01" name="avg_soundness" value={formData.avg_soundness} readOnly className="form-control-vitrag" style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderTest3Fineness = () => (
    <Row className="mb-4">
      <Col>
        <Card className="card-vitrag shadow-sm">
          <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
            <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
              <FontAwesomeIcon icon={faTable} className="me-2" />
              Test Results
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '60%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Number of Tests</th>
                    <th style={{ width: '13.33%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>1</th>
                    <th style={{ width: '13.33%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>2</th>
                    <th style={{ width: '13.33%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>3</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Weight of Cement sample taken (gm) (A)</td>
                    {[1,2,3].map(i => (
                      <td key={i} style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control size="sm" type="number" step="0.001" name={`cement_weight_0${i}`} value={formData[`cement_weight_0${i}`]} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Weight of Residue on 90 micron sieve (gm) (B)</td>
                    {[1,2,3].map(i => (
                      <td key={i} style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control size="sm" type="number" step="0.001" name={`residue_weight_0${i}`} value={formData[`residue_weight_0${i}`]} onChange={handleInputChange} style={{ fontSize: '0.8rem' }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>Percentage of Mass Retained on 90 micron sieve ((B/A)*100)</td>
                    {[1,2,3].map(i => (
                      <td key={i} style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                        <Form.Control size="sm" type="number" step="0.01" name={`mass_percentage_0${i}`} value={formData[`mass_percentage_0${i}`]} readOnly style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                      </td>
                    ))}
                  </tr>
                  <tr className="table-info">
                    <td className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', fontSize: '1.1rem' }}>Average of Residue</td>
                    <td colSpan="3" className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}>
                      <Form.Control size="sm" type="number" step="0.01" name="avg_residue" value={formData.avg_residue} readOnly className="form-control-vitrag" style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderCurrentTest = () => {
    switch (currentTest) {
      case 1:
        return renderTest1SpecificGravity();
      case 2:
        return renderTest2Soundness();
      case 3:
        return renderTest3Fineness();
      case 4:
        return (
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Test Results - Consistency
                  </h5>
                </Card.Header>
                <Card.Body>
                  {/* Section 1: Standard Consistency Test */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">1. Standard Consistency Test - (IS 4031 (Part 4))</h6>
                    <div className="table-responsive">
                      <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                        <thead className="table-dark">
                          <tr>
                            <th style={{ width: '15%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Sr. No</th>
                            <th style={{ width: '25%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Weight of Cement (gm)</th>
                            <th style={{ width: '20%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Weight of Water (gm)</th>
                            <th style={{ width: '25%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Height of Penetrated (mm)</th>
                            <th style={{ width: '15%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Standard Consistency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[1, 2, 3].map((row) => (
                            <tr key={row}>
                              <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>{row}</td>
                              <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.001"
                                  name={`cement_weight_consistency_${row}`}
                                  value={formData[`cement_weight_consistency_${row}`] || ''}
                                  onChange={handleInputChange}
                                  style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                />
                              </td>
                              <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.1"
                                  name={`water_percentage_${row}`}
                                  value={formData[`water_percentage_${row}`] || ''}
                                  onChange={handleInputChange}
                                  style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                />
                              </td>
                              <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="0.1"
                                  name={`penetration_height_${row}`}
                                  value={formData[`penetration_height_${row}`] || ''}
                                  onChange={handleInputChange}
                                  style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                />
                              </td>
                              {row === 1 && (
                                <td
                                  rowSpan={3}
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #dee2e6',
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    step="0.01"
                                    name="standard_consistency"
                                    value={formData.standard_consistency || ''}
                                    readOnly
                                    style={{ fontSize: '0.8rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}
                                  />
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 2: Initial and Final Setting Time */}
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">2. Initial and Final Setting Time - IS 4031 (Part 5)</h6>
                    
                    {/* Subsection I: Initial Setting Time */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-white mb-3">I) Initial Setting Time</h6>

                      <div className="table-responsive">
                        <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                          <thead className="table-dark">
                            <tr>
                              <th style={{ width: '20%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Sr. No.</th>
                              <th style={{ width: '40%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Time</th>
                              <th style={{ width: '40%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Height of Penetrated(mm)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[1, 2, 3].map((row) => (
                              <tr key={row}>
                                <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>{row}</td>
                                <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                  <Form.Control
                                    size="sm"
                                    type="time"
                                    name={`initial_time_${row}`}
                                    value={formData[`initial_time_${row}`] || ''}
                                    onChange={handleInputChange}
                                    style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                  />
                                </td>
                                <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    step="0.1"
                                    name={`initial_penetration_${row}`}
                                    value={formData[`initial_penetration_${row}`] || ''}
                                    onChange={handleInputChange}
                                    style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Observation Field for Initial Setting Time */}
                      <Form.Group className="mt-3">
                        <Form.Label>Observation Initial Setting Time</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="initial_observation"
                          value={formData.initial_observation || ''}
                          onChange={handleInputChange}
                          placeholder=""
                          style={{ fontSize: '0.8rem', borderColor: 'white' }}
                        />
                      </Form.Group>
                    </div>

                    {/* Subsection II: Final Setting Time */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-white mb-3">II) Final Setting Time</h6>

                      <div className="table-responsive">
                        <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                          <thead className="table-dark">
                            <tr>
                              <th style={{ width: '20%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Sr. No.</th>
                              <th style={{ width: '40%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Time</th>
                              <th style={{ width: '40%', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Height of Penetrated(mm)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[1, 2, 3].map((row) => (
                              <tr key={row}>
                                <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>{row}</td>
                                <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                  <Form.Control
                                    size="sm"
                                    type="time"
                                    name={`final_time_${row}`}
                                    value={formData[`final_time_${row}`] || ''}
                                    onChange={handleInputChange}
                                    style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                  />
                                </td>
                                <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    step="0.1"
                                    name={`final_penetration_${row}`}
                                    value={formData[`final_penetration_${row}`] || ''}
                                    onChange={handleInputChange}
                                    style={{ fontSize: '0.8rem', textAlign: 'center' }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Observation Field for Final Setting Time */}
                      <Form.Group className="mt-3">
                        <Form.Label>Observation Final Setting Time</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="final_observation"
                          value={formData.final_observation || ''}
                          onChange={handleInputChange}
                          placeholder=""
                          style={{ fontSize: '0.8rem', borderColor: 'white' }}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );
      case 5:
        return (
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Test Results
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                      <thead className="table-dark">
                        <tr>
                          <th rowSpan="2" style={{ width: '50px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Sr. No</th>
                          <th colSpan="3" style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontSize: '1rem' }}>Size</th>
                          <th rowSpan="2" style={{ width: '90px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Area (mm²)</th>
                          <th rowSpan="2" style={{ width: '120px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Cube Weight (Kg)</th>
                          <th rowSpan="2" style={{ width: '120px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Density (Kg/m³)</th>
                          <th rowSpan="2" style={{ width: '120px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Maximum Load (N)</th>
                          <th rowSpan="2" style={{ width: '150px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '1rem' }}>Compressive Strength (Mpa)</th>
                        </tr>
                        <tr>
                          <th style={{ width: '100px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem' }}>L (mm)</th>
                          <th style={{ width: '100px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem' }}>B (mm)</th>
                          <th style={{ width: '100px', padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem' }}>H (mm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3].map((row) => (
                          <tr key={row}>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center', fontWeight: 'bold' }}>{row}</td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`length_${row}`}
                                value={formData[`length_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.8rem', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`breadth_${row}`}
                                value={formData[`breadth_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.8rem', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`height_${row}`}
                                value={formData[`height_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.8rem', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                name={`area_${row}`}
                                value={formData[`area_${row}`]}
                                readOnly
                                style={{ fontSize: '0.8rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.001"
                                name={`weight_${row}`}
                                value={formData[`weight_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.8rem' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`density_${row}`}
                                value={formData[`density_${row}`]}
                                readOnly
                                style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`load_max_${row}`}
                                value={formData[`load_max_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.8rem' }}
                              />
                            </td>
                            <td style={{ padding: '6px', border: '1px solid #dee2e6' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                name={`compressive_strength_${row}`}
                                value={formData[`compressive_strength_${row}`]}
                                readOnly
                                style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }}
                              />
                            </td>
                          </tr>
                        ))}
                        {/* Average Row */}
                        <tr className="table-info">
                          <td colSpan="8" className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', fontSize: '1.1rem' }}>Average</td>
                          <td className="text-center fw-bold" style={{ padding: '6px', border: '1px solid #dee2e6', backgroundColor: '#1C2333' }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              name="avg_compressive_strength"
                              value={formData.avg_compressive_strength}
                              readOnly
                              className="form-control-vitrag"
                              style={{ fontSize: '0.8rem', backgroundColor: '#f8f9fa' }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        );
      default:
        return null;
    }
  };

  if (showPreview) {
    return (
      <div>
            <div style={{ backgroundColor: '#6c757d', padding: '2rem 0', borderRadius: '15px' }}>
          <Container>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faIndustry} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                  <div>
                    <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                      Cement Testing - Preview
                    </h1>
                    <p className="mb-0 mt-2" style={{ color: 'white' }}>
                      Review all details before final submission
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button onClick={handleEditForm} variant="light" className="btn-vitrag-primary me-2">
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Edit Form
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="py-4">
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General Information
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
              <Row>
                    <Col md={6}>
                      <p><strong>Customer:</strong> {formData.customer_id === '1' ? 'ABC Construction Ltd.' : formData.customer_id === '2' ? 'XYZ Builders' : formData.customer_id === '3' ? 'DEF Infrastructure' : formData.customer_id}</p>
                      <p><strong>Sample Description:</strong> {formData.sample_description}</p>
                      <p><strong>Date of Receipt:</strong> {formData.date_of_receipt}</p>
                      <p><strong>Cement Type:</strong> {formData.cement_type}</p>
                      <p><strong>Test Method:</strong> {formData.test_method}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Sample Test Code:</strong> {formData.sample_test_code}</p>
                      <p><strong>Date of Testing:</strong> {formData.date_of_testing}</p>
                      <p><strong>Cement Grade:</strong> {formData.cement_grade}</p>
                      <p><strong>Environmental Conditions:</strong> {formData.environmental_conditions}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col className="text-center">
              <Button onClick={handleEditForm} variant="secondary" size="lg" className="me-3">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Edit Form
              </Button>
              <Button onClick={handleConfirmSubmit} variant="success" size="lg" className="me-3">
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Confirm & Save
              </Button>
              <Button as={Link} to="/other-services" variant="danger" size="lg" className="px-5">
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div>
            <div style={{ backgroundColor: '#6c757d', padding: '2rem 0', borderRadius: '15px' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faIndustry} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    Cement Testing - {currentTest}
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    {testTypes.find(t => t.id === currentTest)?.description}
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button as={Link} to="/other-services" variant="light" className="btn-vitrag-primary">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Other Services
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        <Form onSubmit={handleSubmit}>
          {renderTestNavigation()}

          {/* General Information */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General Information
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
              <Row>
                    {/* Left Column */}
                    <Col md={6}>
                      {/* Only show Customer Name in the first selected test */}
                      {currentTest === firstTestNumber && (
                        <Form.Group className="mb-3">
                          <Form.Label>Customer Name *</Form.Label>
                          {customersLoading ? (
                            <div className="d-flex align-items-center">
                              <Spinner animation="border" size="sm" className="me-2" />
                              <span>Loading customers...</span>
                            </div>
                          ) : customersError ? (
                            <Alert variant="warning" className="mb-0">
                              {customersError}
                            </Alert>
                          ) : (
                            <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                              <Form.Control
                                type="text"
                                name="customerName"
                                value={customerSearchTerm}
                                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                onClick={handleCustomerInputClick}
                                placeholder="Search and select customer..."
                                required
                                autoComplete="off"
                                readOnly
                                className="form-control-vitrag"
                                style={{ 
                                  cursor: 'pointer'
                                }}
                              />
                              {showCustomerDropdown && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: '#2d3748',
                                    border: '2px solid #4a5568',
                                    borderTop: 'none',
                                    borderRadius: '0 0 10px 10px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                                  }}
                                >
                                  {/* Search input inside dropdown */}
                                  <div style={{ padding: '15px', borderBottom: '1px solid #4a5568' }}>
                                    <Form.Control
                                      type="text"
                                      placeholder="Search customers..."
                                      value={dropdownSearchTerm}
                                      onChange={handleDropdownSearch}
                                      autoComplete="off"
                                      style={{
                                        backgroundColor: '#1a202c',
                                        border: '2px solid #4a5568',
                                        borderRadius: '8px',
                                        color: '#e2e8f0',
                                        padding: '10px 12px',
                                        fontSize: '0.9rem',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = '#FFA500';
                                        e.target.style.boxShadow = '0 0 0 0.2rem rgba(255, 165, 0, 0.25)';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#4a5568';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                    />
                                  </div>
                                  
                                  {/* Customer list */}
                                  {filteredCustomers.length > 0 ? (
                                    filteredCustomers.map((customer) => (
                                      <div
                                        key={customer.id}
                                        onClick={() => handleCustomerSelect(customer)}
                                        style={{
                                          padding: '10px 15px',
                                          cursor: 'pointer',
                                          borderBottom: '1px solid #6c757d',
                                          color: 'white',
                                          backgroundColor: 'transparent'
                                        }}
                                      >
                                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                          {customer.name}
                                        </div>
                                        <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                          {customer.phone && `${customer.phone} • `}
                                          {customer.city && customer.city}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div
                                      style={{
                                        padding: '15px',
                                        color: '#adb5bd',
                                        textAlign: 'center'
                                      }}
                                    >
                                      {dropdownSearchTerm.trim() !== '' 
                                        ? `No customers found matching "${dropdownSearchTerm}"`
                                        : 'No customers available'
                                      }
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </Form.Group>
                      )}
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Number *</Form.Label>
                        <Form.Control type="text" name="reference_number" value={formData.reference_number} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>ULR Number *</Form.Label>
                        <Form.Control type="text" name="url_number" value={formData.url_number} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Job Code Number *</Form.Label>
                        <Form.Control type="text" name="job_code_number" value={formData.job_code_number} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Sample Description *</Form.Label>
                        <Form.Control as="textarea" rows={3} name="sample_description" value={formData.sample_description} onChange={handleInputChange} placeholder="Cement" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Sample Test Code Number *</Form.Label>
                        <Form.Control type="text" name="sample_test_code" value={formData.sample_test_code} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Receipt *</Form.Label>
                        <Form.Control type="date" name="date_of_receipt" value={formData.date_of_receipt} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Testing *</Form.Label>
                        <Form.Control type="date" name="date_of_testing" value={formData.date_of_testing} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Period of Testing *</Form.Label>
                        <Row>
                          <Col md={5}>
                            <Form.Control 
                              type="date" 
                              name="period_of_testing_start" 
                              value={formData.period_of_testing_start} 
                              onChange={(e) => handlePeriodOfTestingChange('period_of_testing_start', e.target.value)} 
                              placeholder="Start Date" 
                              required 
                            />
                          </Col>
                          <Col md={2} className="text-center d-flex align-items-center justify-content-center">
                            <span style={{ color: 'white', fontWeight: 'bold' }}>to</span>
                          </Col>
                          <Col md={5}>
                            <Form.Control 
                              type="date" 
                              name="period_of_testing_end" 
                              value={formData.period_of_testing_end} 
                              onChange={(e) => handlePeriodOfTestingChange('period_of_testing_end', e.target.value)} 
                              placeholder="End Date" 
                              required 
                            />
                          </Col>
                        </Row>
                        {formData.period_of_testing && (
                          <Form.Text className="text-muted" style={{ color: '#adb5bd', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {formData.period_of_testing}
                          </Form.Text>
                        )}
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Location of Testing *</Form.Label>
                        <Form.Control type="text" name="location_of_testing" value={formData.location_of_testing} onChange={handleInputChange} placeholder="Lab" required />
                      </Form.Group>
                    </Col>
                    
                    {/* Right Column */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Type of Cement *</Form.Label>
                        <Form.Select
                          name="cement_type"
                          value={formData.cement_type}
                          onChange={handleInputChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select type of cement</option>
                          {CEMENT_TYPE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Grade of Cement *</Form.Label>
                        <Form.Control type="text" name="cement_grade" value={formData.cement_grade} onChange={handleInputChange} placeholder="Enter cement grade" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Condition of Sample *</Form.Label>
                        <Form.Control type="text" name="condition_of_sample" value={formData.condition_of_sample} onChange={handleInputChange} placeholder="Acceptable" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Test Method *</Form.Label>
                        <Form.Control type="text" name="test_method" value={formData.test_method} onChange={handleInputChange} placeholder="Enter test method" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Environmental Conditions *</Form.Label>
                        <Form.Control type="text" name="environmental_conditions" value={formData.environmental_conditions} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Humidity *</Form.Label>
                        <Form.Control type="text" name="humidity" value={formData.humidity} onChange={handleInputChange} placeholder="" required />
                      </Form.Group>
                      
                      {currentTest === 5 && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Date of Casting *</Form.Label>
                            <Form.Control type="date" name="date_of_casting" value={formData.date_of_casting} onChange={handleInputChange} required />
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Curing Conditions *</Form.Label>
                            <Form.Control type="text" name="curing_conditions" value={formData.curing_conditions} onChange={handleInputChange} placeholder="" required />
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Machine used for Testing *</Form.Label>
                            <Form.Control type="text" name="machine_used_for_testing" value={formData.machine_used_for_testing} onChange={handleInputChange} placeholder="" required />
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Age of Cube *</Form.Label>
                            <Form.Select name="age_of_cube" value={formData.age_of_cube} onChange={handleInputChange} className="form-select" required>
                              <option value="">Select age of cube</option>
                              <option value="3 days">3 days</option>
                              <option value="7 days">7 days</option>
                              <option value="28 days">28 days</option>
                            </Form.Select>
                          </Form.Group>
                        </>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {selectedTests.length === 0 ? (
            <Row className="mb-4">
              <Col>
                <Alert variant="warning" className="text-center">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  No tests selected. Please go back and select at least one test to perform.
                </Alert>
              </Col>
            </Row>
          ) : (
            renderCurrentTest()
          )}

          {/* Verification */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Verification
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
              <Row>
                    <Col md={4}>
                      <h6>Tested By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control type="text" name="tested_by_name" value={formData.tested_by_name} onChange={handleInputChange} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control type="date" name="tested_by_date" value={formData.tested_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Checked By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control type="text" name="checked_by_name" value={formData.checked_by_name} onChange={handleInputChange} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control type="date" name="checked_by_date" value={formData.checked_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Verified By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" name="verified_by_name" value={formData.verified_by_name} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control type="date" name="verified_by_date" value={formData.verified_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Remarks */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faComment} className="me-2" />
                    Remarks
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
                    <Form.Control as="textarea" rows={4} name="remarks" value={formData.remarks} onChange={handleInputChange} placeholder="" />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Review & Authorization */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Reviewed By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
                    <Form.Label>Select Reviewer:</Form.Label>
                    {loadingReviewers ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading reviewers...</span>
                      </div>
                    ) : (
                      <Form.Select 
                        id="reviewer_select" 
                        onChange={handleReviewerChange}
                        value={reviewerInfo.name}
                        className="form-select"
                        required
                      >
                        {reviewers.map((reviewer) => (
                          <option 
                            key={reviewer.id} 
                            value={reviewer.name} 
                            data-name={reviewer.name} 
                            data-designation={reviewer.designation} 
                            data-graduation={reviewer.graduation}
                          >
                            {reviewer.name} - {reviewer.designation}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Authorized By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
            <Form.Group className="mb-3">
                    <Form.Label>Authorized By:</Form.Label>
                    <Form.Control type="text" name="authorized_by" value={formData.authorized_by} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col className="text-center">
              <Button type="submit" variant="primary" size="lg" className="me-3">
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save Test Data
              </Button>
              <Button as={Link} to="/other-services" variant="secondary" size="lg" className="px-5">
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default CementTestingForm;
